import { useEffect, useMemo, useState } from 'react';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { mfgApi } from '@/pages/mfg/mfgApi';
import { mktApi, errMsg } from './marketingApi';
import { FULFILLMENT } from './mktShared';

export default function HandoffDialog({ open, onOpenChange, order, onSaved }) {
  const [departments, setDepartments] = useState([]);
  const [deptId, setDeptId] = useState('');
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!open) return;
    mfgApi.departments().then((d) => {
      setDepartments(d || []);
      if ((d || []).length === 1) setDeptId(d[0].id);
    }).catch(() => toast.error('部門の取得に失敗しました'));
  }, [open]);

  const split = useMemo(() => {
    const lines = order?.lines || [];
    return {
      production: lines.filter((l) => l.fulfillment === 'production'),
      stock: lines.filter((l) => l.fulfillment === 'stock'),
    };
  }, [order]);

  const stockOnly = split.production.length === 0;

  const save = async () => {
    if (!deptId) { toast.error('部門を選択してください'); return; }
    setSaving(true);
    try {
      const updated = await mktApi.sendToSeizo(order.id, { department_id: deptId });
      toast.success(stockOnly ? '出荷手配へ進めました（検査待ち）' : `製造依頼を送信しました（オーダー ${split.production.length}件）`);
      onSaved?.(updated);
      onOpenChange(false);
    } catch (e) { toast.error(errMsg(e)); }
    finally { setSaving(false); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md" data-testid="mkt-handoff-dialog">
        <DialogHeader><DialogTitle>{stockOnly ? '出荷手配へ' : '製造依頼（Seizoへ送信）'} — {order?.order_no}</DialogTitle></DialogHeader>
        <div className="space-y-3">
          <div>
            <label className="text-xs font-medium text-zinc-600">担当部門 <span className="text-red-500">*</span></label>
            <Select value={deptId} onValueChange={setDeptId}>
              <SelectTrigger className="mt-1" data-testid="mkt-handoff-department"><SelectValue placeholder="部門を選択" /></SelectTrigger>
              <SelectContent>
                {departments.map((d) => <SelectItem key={d.id} value={d.id}>{d.name}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div className="rounded-lg border border-zinc-200 bg-zinc-50/60 p-3 space-y-1.5 text-xs">
            {split.production.length > 0 && (
              <div>
                <span className={`rounded-full border px-2 py-0.5 text-[10px] font-medium ${FULFILLMENT.production.cls}`}>製造 {split.production.length}件</span>
                <span className="ml-2 text-zinc-500">製造オーダーを作成します（部品不足は資材へ自動連携）</span>
              </div>
            )}
            {split.stock.length > 0 && (
              <div>
                <span className={`rounded-full border px-2 py-0.5 text-[10px] font-medium ${FULFILLMENT.stock.cls}`}>在庫出荷 {split.stock.length}件</span>
                <span className="ml-2 text-zinc-500">在庫は出荷時に引き落とされます</span>
              </div>
            )}
            <p className="text-[10px] text-amber-600 pt-1">⚠ 製造依頼は1回のみ実行できます。送信後は明細を編集できません。</p>
          </div>
          <div className="flex justify-end gap-2">
            <Button variant="outline" onClick={() => onOpenChange(false)} data-testid="mkt-handoff-cancel-btn">キャンセル</Button>
            <Button onClick={save} disabled={saving} className="bg-teal-700 hover:bg-teal-800" data-testid="mkt-handoff-send-btn">
              {saving ? '送信中…' : (stockOnly ? '出荷手配へ進める' : '製造依頼を送信')}
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
